import { z } from 'zod';
import { uuidSchema, isoDateSchema, isoDateTimeSchema, timestampedSchema } from '../schemas/common';

export const chargeCategorySchema = z.enum([
  'tuition',
  'costume',
  'ticket',
  'competition_entry',
  'late_fee',
  'drop_credit',
  'camp',
  'workshop',
  'private_lesson',
  'pass',
  'recital_package',
  'custom',
]);
export type ChargeCategory = z.infer<typeof chargeCategorySchema>;

export const chargeStatusSchema = z.enum(['pending', 'paid', 'partial', 'void', 'written_off']);
export type ChargeStatus = z.infer<typeof chargeStatusSchema>;

export const paymentMethodSchema = z.enum(['stripe_card', 'stripe_bank', 'cash', 'check', 'other']);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

export const paymentStatusSchema = z.enum(['pending', 'succeeded', 'failed', 'refunded']);
export type PaymentStatus = z.infer<typeof paymentStatusSchema>;

// Billing accounts (Sprint 12 — multi-ledger per family) -------------------

export const billingAccountKindSchema = z.enum(['studio', 'competition', 'custom']);
export type BillingAccountKind = z.infer<typeof billingAccountKindSchema>;

export const billingAccountSchema = timestampedSchema.extend({
  family_id: uuidSchema,
  kind: billingAccountKindSchema,
  name: z.string(),
  active: z.boolean(),
});
export type BillingAccount = z.infer<typeof billingAccountSchema>;

export const billingAccountInputSchema = z.object({
  family_id: uuidSchema,
  kind: billingAccountKindSchema,
  name: z.string().trim().min(1).max(80),
  active: z.boolean().default(true),
});
export type BillingAccountInput = z.infer<typeof billingAccountInputSchema>;

// Per-account balance (replaces the old family-only rollup).
export const accountBalanceSchema = z.object({
  account_id: uuidSchema,
  family_id: uuidSchema,
  kind: billingAccountKindSchema,
  name: z.string(),
  active: z.boolean(),
  outstanding_cents: z.number().int(),
  open_count: z.number().int().nonnegative(),
  overdue_count: z.number().int().nonnegative(),
});
export type AccountBalance = z.infer<typeof accountBalanceSchema>;

// Charges --------------------------------------------------------------------

export const chargeInputSchema = z.object({
  family_id: uuidSchema,
  // Optional on input. If omitted, the DB trigger added in migration 0023
  // defaults it to the family's studio account (or the competition account
  // when category=competition_entry). Admins typically pick explicitly.
  account_id: uuidSchema.optional(),
  dancer_id: uuidSchema.optional(),
  enrollment_id: uuidSchema.optional(),
  competition_routine_id: uuidSchema.optional(),
  category: chargeCategorySchema,
  description: z.string().trim().min(1).max(500),
  amount_cents: z.number().int(),
  due_date: isoDateSchema.optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type ChargeInput = z.infer<typeof chargeInputSchema>;

export const chargeSchema = timestampedSchema.extend({
  family_id: uuidSchema,
  account_id: uuidSchema,
  dancer_id: uuidSchema.nullable(),
  enrollment_id: uuidSchema.nullable(),
  competition_routine_id: uuidSchema.nullable(),
  category: chargeCategorySchema,
  description: z.string(),
  amount_cents: z.number().int(),
  due_date: isoDateSchema.nullable(),
  billed_at: isoDateTimeSchema.nullable(),
  status: chargeStatusSchema,
  stripe_invoice_id: z.string().nullable(),
  stripe_payment_intent_id: z.string().nullable(),
  notes: z.string().nullable(),
});
export type Charge = z.infer<typeof chargeSchema>;

// Payments ------------------------------------------------------------------

export const paymentInputSchema = z.object({
  family_id: uuidSchema,
  account_id: uuidSchema,
  amount_cents: z.number().int().positive(),
  method: paymentMethodSchema,
  reference: z.string().trim().max(120).optional(),
  notes: z.string().trim().max(2000).optional(),
});
export type PaymentInput = z.infer<typeof paymentInputSchema>;

export const paymentSchema = timestampedSchema.extend({
  family_id: uuidSchema,
  amount_cents: z.number().int().positive(),
  method: paymentMethodSchema,
  status: paymentStatusSchema,
  stripe_payment_intent_id: z.string().nullable(),
  stripe_charge_id: z.string().nullable(),
  reference: z.string().nullable(),
  paid_at: isoDateTimeSchema.nullable(),
  notes: z.string().nullable(),
});
export type Payment = z.infer<typeof paymentSchema>;

// Late fee rules -----------------------------------------------------------

export const lateFeeRuleInputSchema = z
  .object({
    name: z.string().trim().min(1).max(120),
    grace_period_days: z.number().int().min(0).max(90).default(5),
    fee_cents: z.number().int().positive().optional(),
    fee_percent_bp: z.number().int().min(1).max(5000).optional(),
    applies_to: z.array(chargeCategorySchema).min(1).default(['tuition']),
    active: z.boolean().default(true),
  })
  .refine((r) => (r.fee_cents !== undefined) !== (r.fee_percent_bp !== undefined), {
    message: 'Provide exactly one of fee_cents or fee_percent_bp',
    path: ['fee_cents'],
  });
export type LateFeeRuleInput = z.infer<typeof lateFeeRuleInputSchema>;
